import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from "../Auth/Auth.module.css";

const Register = () => {
  const { register, sendOtp, verifyOtp, resendOtp, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Input handler
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!form.email) {
      setNotification({
        type: "error",
        message: "Please enter your email first",
      });
      return;
    }
    const res = await sendOtp(form.email, "register");
    if (res.ok) {
      setOtpSent(true);
      setNotification({ type: "success", message: "OTP sent successfully" });
    } else {
      setNotification({ type: "error", message: res.message });
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!form.email || !form.otp) {
      setNotification({ type: "error", message: "Email and OTP are required" });
      return;
    }
    const res = await verifyOtp(form.email, form.otp, "register");
    if (res.ok) {
      setOtpVerified(true);
      setNotification({
        type: "success",
        message: "OTP verified successfully",
      });
    } else {
      setNotification({ type: "error", message: res.message });
    }
  };

  // Register
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!otpVerified) {
      setNotification({ type: "error", message: "Please verify OTP first" });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setNotification({ type: "error", message: "Passwords do not match" });
      return;
    }

    const res = await register(
      form.username,
      form.email,
      form.password,
      form.otp,
    );

    if (res.ok) {
      setNotification({
        type: "success",
        message: "Account created successfully",
      });
      // Redirect to home
      navigate("/");
    } else {
      setNotification({ type: "error", message: res.message });
    }
  };

  return (
    <div className={styles.authWrapper}>
      <div className={styles.authCard}>
        <h1 className={styles.authTitle}>Create Your PixelMoon Account</h1>
        <p className={styles.subtitle}>Join the future of gaming</p>

        {notification && (
          <div
            className={
              notification.type === "success"
                ? styles.successMsg
                : styles.errorMsg
            }
          >
            {notification.message}
          </div>
        )}

        <form className={styles.authForm} onSubmit={handleRegister}>
          {/* Username */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Username</label>
            <input
              className={styles.formInput}
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              placeholder="Username"
              required
            />
          </div>

          {/* Email */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Email</label>
            <input
              className={styles.formInput}
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              required
              disabled={otpSent}
            />
          </div>

          {/* Password */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Password</label>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Create a password"
                required
                minLength="6"
              />
              <button
                type="button"
                className={styles.showBtn}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Confirm Password</label>
            <div className={styles.passwordWrapper}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Confirm your password"
                required
                minLength="6"
              />
              <button
                type="button"
                className={styles.showBtn}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* OTP Section */}
          {!otpSent ? (
            <button
              type="button"
              className={styles.authButton}
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          ) : (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>OTP</label>
              <input
                className={styles.formInput}
                type="text"
                name="otp"
                value={form.otp}
                onChange={handleChange}
                required
              />
              {!otpVerified ? (
                <>
                  <button
                    type="button"
                    className={styles.authButton}
                    onClick={handleVerifyOtp}
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => resendOtp(form.email, "register")}
                  >
                    Resend OTP
                  </button>
                </>
              ) : (
                <span className={styles.successMsg}>OTP Verified</span>
              )}
            </div>
          )}

          {/* Final Register */}
          <button
            type="submit"
            className={styles.authButton}
            disabled={loading || !otpVerified}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <p className={styles.authLink}>
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
